import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// leaflet.markercluster's UMD build expects a global `L` when it can't
// reliably detect the module system under Vite's bundling -- without this,
// L.markerClusterGroup comes back undefined at runtime. Must run before the
// side-effect import below.
window.L = L;
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

import { api } from '../../api.js';
import { Spinner, ErrorNote, CollapsibleSection } from '../../components.jsx';

// One layer per stage jobmap watches, in pipeline order -- that order is also
// the order they're listed in the key. Colours are kept as inline styles (not
// Tailwind classes) since this HTML is built at runtime for Leaflet's own
// divIcon/layer control, outside Tailwind's static content scan.
const LAYER_META = {
  quoting: { label: 'Needs quoting', color: '#f4b749' }, // amber
  quoted: { label: 'Quoted', color: '#8e5ea2' }, // purple
  in_progress: { label: 'In progress', color: '#294566' }, // navy
};

// The layer control's label, doubling as the map's colour key: a swatch the
// same colour as that layer's pins, the name, and how many pins it has.
function keyLabel(meta, count) {
  return (
    `<span style="display:inline-block;width:12px;height:12px;border-radius:50%;` +
    `background:${meta.color};box-shadow:0 0 0 1px rgba(0,0,0,0.3);` +
    `vertical-align:-1px;margin-right:6px;"></span>${meta.label} (${count})`
  );
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function clusterIconFactory(color) {
  return (cluster) =>
    L.divIcon({
      html: `<div style="background:${color};width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:bold;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);">${cluster.getChildCount()}</div>`,
      className: '',
      iconSize: L.point(36, 36),
    });
}

function popupHtml(p) {
  const title = p.url
    ? `<a href="${escapeHtml(p.url)}" target="_blank" rel="noreferrer"><strong>${escapeHtml(p.label)}</strong></a>`
    : `<strong>${escapeHtml(p.label)}</strong>`;
  return `${title}<br/>${escapeHtml(p.suburb)}, ${escapeHtml(p.state)}`;
}

// The same global set of pins for everyone -- no per-user "view as" the way
// Sales to-do has. Rendered as a sibling inside that same section, below the
// task table.
export default function JobMapWidget() {
  const [open, setOpen] = useState(false); // collapsed on load
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const [pins, setPins] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || pins) return; // don't fetch until first expanded, and only ever once
    api.get('/api/widgets/jobmap-pins').then(setPins).catch((e) => setError(e.message));
  }, [open, pins]);

  useEffect(() => {
    if (!pins || !mapElRef.current || mapRef.current) return; // StrictMode-safe: only ever init once
    const map = L.map(mapElRef.current).setView([-25.0, 135.0], 4); // Australia-wide default
    mapRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // One cluster group per layer, colour-coded to match, so both the
    // clustered badge and the individual marker read consistently at any
    // zoom level.
    const overlays = {};
    for (const [layerKey, meta] of Object.entries(LAYER_META)) {
      const group = L.markerClusterGroup({ iconCreateFunction: clusterIconFactory(meta.color) });
      const layerPins = pins.filter((p) => p.layer === layerKey);
      layerPins.forEach((p) => {
        const marker = L.circleMarker([p.lat, p.lng], {
          radius: 8,
          color: '#fff',
          weight: 1,
          fillColor: meta.color,
          fillOpacity: 0.9,
        });
        marker.bindPopup(popupHtml(p));
        group.addLayer(marker);
      });
      group.addTo(map);
      overlays[keyLabel(meta, layerPins.length)] = group;
    }
    L.control.layers(null, overlays, { collapsed: false }).addTo(map);

    if (pins.length > 0) {
      map.fitBounds(
        L.latLngBounds(pins.map((p) => [p.lat, p.lng])),
        { padding: [24, 24], maxZoom: 12 }
      );
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [pins]);

  return (
    <div className="card mt-4 p-4">
      <CollapsibleSection title="Job map" open={open} onToggle={() => setOpen((o) => !o)} className="">
        <p className="mb-3 text-xs text-steel">
          Open jobs by stage — needs quoting, quoted, and in progress. The key in the corner of the map doubles as
          the layer toggles. Not a map of all current work.
        </p>
        {error && <ErrorNote>Couldn't reach jobmap ({error})</ErrorNote>}
        {!pins && !error && <Spinner label="Loading job map…" />}
        {pins && pins.length === 0 && <p className="text-sm text-steel">No open jobs with a suburb yet.</p>}
        <div ref={mapElRef} className={pins && pins.length > 0 ? 'h-96 w-full rounded-lg' : 'hidden'} />
      </CollapsibleSection>
    </div>
  );
}
